/// Thin HTTP client over goastern-auth, paired with a session store.
/// Handles four things:
///   - sign-in via magic-link, Apple SIWA web, or Google OAuth web
///     (`appleSignInUrl()` / `googleSignInUrl()` return the URLs to
///     navigate to)
///   - consume the URL-fragment callback after the user comes back
///     (`#access=…&refresh=…&expires=…&audience=…`) — same shape for
///     all three providers
///   - keep the access token fresh + authorise outgoing requests
///   - discover which providers the auth worker actually has wired
///     up (`providers()`) so UI can hide buttons that would 503

import { SessionStorage, defaultStorage } from "./storage";
import type {
  CallbackFragment,
  IdentityRow,
  LinkIntentResponse,
  MeResponse,
  ProvidersResponse,
  StoredSession,
} from "./types";

export interface AuthClientOptions {
  /// Base URL of the goastern-auth Worker, e.g.
  /// `https://auth.goastern.com` (production) or
  /// `https://goastern-auth-staging.<account>.workers.dev` (staging).
  authBaseUrl: string;
  /// ClientApp key registered in goastern-auth's audience map. For
  /// nav-games this is always `"nav"`.
  app: "nav";
  /// The platform this app is running on. Drives the JWT audience.
  /// In a Capacitor build at startup, pick based on platform; on web
  /// always `"web"`.
  platform: "web" | "ios" | "android";
  /// Where to send the user after sign-in. Must be allowlisted in
  /// goastern-auth's `WEB_RETURN_URL_ALLOWLIST` env var.
  returnUrl: string;
  /// Override for tests; defaults to the global fetch.
  fetchImpl?: typeof fetch;
  /// Override for tests; defaults to `localStorage`-backed storage.
  storage?: SessionStorage;
}

export interface MagicRequestInput {
  email: string;
  displayName?: string;
}

export class AuthClient {
  private readonly storage: SessionStorage | null;
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly options: AuthClientOptions) {
    this.storage = options.storage ?? defaultStorage();
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  }

  /// Returns the saved session, if any. Doesn't check expiry — the
  /// caller asks via `isExpired()` or calls `refresh()` proactively
  /// (cleaner than throwing inside a getter).
  session(): StoredSession | null {
    return this.storage?.load() ?? null;
  }

  /// True when the access token is past its `exp` (mind clock skew —
  /// we consider tokens "expired" 30s before they actually expire so
  /// in-flight requests aren't surprised by a server-side rejection).
  isExpired(now: Date = new Date()): boolean {
    const s = this.session();
    if (!s) return true;
    return s.accessExpiresAt - 30 <= Math.floor(now.getTime() / 1000);
  }

  /// Sign out: forget local tokens. Doesn't revoke server-side; the
  /// JWT remains valid until natural expiry. Call `/auth/revoke` with
  /// the refresh token if you want server-side revocation too.
  signOut(): void {
    this.storage?.clear();
  }

  /// POST `/auth/magic/request`. The user will receive an email; the
  /// link in the email leads back to `options.returnUrl` carrying the
  /// tokens in the URL fragment for `consumeCallback()` to pick up.
  async requestMagicLink(input: MagicRequestInput): Promise<void> {
    const resp = await this.fetchImpl(`${this.options.authBaseUrl}/auth/magic/request`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        app: this.options.app,
        platform: this.options.platform,
        email: input.email,
        ...(input.displayName !== undefined ? { displayName: input.displayName } : {}),
        returnUrl: this.options.returnUrl,
      }),
    });
    if (!resp.ok) {
      const body = await safeJson(resp);
      throw new Error(
        `magic_request_failed_${resp.status}: ${JSON.stringify(body)}`,
      );
    }
  }

  /// Exchange the 6-digit numeric companion code (emailed alongside
  /// the magic-link URL) for a session. Used when the link can't be
  /// clicked on the device running the app — e.g. iOS Simulator with
  /// no Mail account, or a paired laptop + phone where mail lives on
  /// the phone. The response shape mirrors the native POST redeem
  /// endpoint, so the resulting session feeds the same storage path
  /// as `consumeCallback()`.
  async redeemMagicCode(code: string): Promise<StoredSession> {
    const resp = await this.fetchImpl(
      `${this.options.authBaseUrl}/auth/magic/redeem-code`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ code }),
      },
    );
    if (!resp.ok) {
      const body = await safeJson(resp);
      throw new Error(
        `magic_redeem_code_failed_${resp.status}: ${JSON.stringify(body)}`,
      );
    }
    const json = (await resp.json()) as {
      accessToken: string;
      expiresAt: number;
      audience: string;
      refreshToken: string;
      refreshExpiresAt: number;
    };
    const session: StoredSession = {
      accessToken: json.accessToken,
      refreshToken: json.refreshToken,
      accessExpiresAt: json.expiresAt,
      refreshExpiresAt: json.refreshExpiresAt,
      audience: json.audience,
    };
    this.storage?.save(session);
    return session;
  }

  /// URL to navigate the browser to in order to start Apple SIWA web
  /// sign-in. After sign-in, the user is redirected to
  /// `options.returnUrl` with tokens in the URL fragment.
  ///
  /// `options.returnUrl` must be allowlisted in goastern-auth's
  /// `WEB_RETURN_URL_ALLOWLIST` env var for the configured `app`, or
  /// the start endpoint 400s before redirecting to Apple.
  appleSignInUrl(): string {
    const params = new URLSearchParams({
      app: this.options.app,
      returnUrl: this.options.returnUrl,
    });
    return `${this.options.authBaseUrl}/auth/apple/web/start?${params.toString()}`;
  }

  /// URL to navigate the browser to in order to start Google OAuth
  /// sign-in. Same allowlist requirement as `appleSignInUrl()`.
  googleSignInUrl(): string {
    const params = new URLSearchParams({
      app: this.options.app,
      returnUrl: this.options.returnUrl,
    });
    return `${this.options.authBaseUrl}/auth/google/start?${params.toString()}`;
  }

  /// `GET /auth/providers?app=<app>` — discovery endpoint. Returns
  /// `{apple, google, magicLink}` booleans reflecting which providers
  /// are currently wired up on the worker. UI hides buttons that
  /// would 503 on tap.
  async providers(): Promise<ProvidersResponse> {
    const resp = await this.fetchImpl(
      `${this.options.authBaseUrl}/auth/providers?app=${this.options.app}`,
    );
    if (!resp.ok) {
      throw new Error(`providers_failed_${resp.status}`);
    }
    return (await resp.json()) as ProvidersResponse;
  }

  /// Read the URL fragment left behind by goastern-auth's magic-link
  /// or Apple SIWA web or Google OAuth web redirect (they all use
  /// the same fragment shape), persist the session, and clear the
  /// hash so the tokens don't stick around in browser history.
  /// Returns the parsed session, or null when the current URL has no
  /// fragment to consume.
  consumeCallback(url: URL = new URL(window.location.href)): StoredSession | null {
    const parsed = parseCallbackFragment(url);
    if (!parsed) return null;
    const session: StoredSession = {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      accessExpiresAt: parsed.expiresAt,
      audience: parsed.audience,
    };
    this.storage?.save(session);
    return session;
  }

  /// Rotate the refresh token (and access token along with it).
  async refresh(): Promise<StoredSession> {
    const current = this.session();
    if (!current) throw new Error("no_session_to_refresh");
    const resp = await this.fetchImpl(`${this.options.authBaseUrl}/auth/refresh`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refreshToken: current.refreshToken }),
    });
    if (!resp.ok) {
      const body = await safeJson(resp);
      // 401 from refresh means the refresh token is gone (revoked /
      // reused / expired). Clear local state so the UI shows sign-in.
      if (resp.status === 401) this.signOut();
      throw new Error(
        `refresh_failed_${resp.status}: ${JSON.stringify(body)}`,
      );
    }
    const json = (await resp.json()) as {
      accessToken: string;
      expiresAt: number;
      audience: string;
      refreshToken: string;
      refreshExpiresAt: number;
    };
    const next: StoredSession = {
      accessToken: json.accessToken,
      refreshToken: json.refreshToken,
      accessExpiresAt: json.expiresAt,
      refreshExpiresAt: json.refreshExpiresAt,
      audience: json.audience,
    };
    this.storage?.save(next);
    return next;
  }

  /// `GET /auth/me` with an auto-refresh on expiry. Returns null when
  /// no session is held (callers can treat that as "unauthenticated").
  async me(): Promise<MeResponse | null> {
    let session = this.session();
    if (!session) return null;
    if (this.isExpired()) {
      try {
        session = await this.refresh();
      } catch {
        return null;
      }
    }
    const resp = await this.fetchImpl(`${this.options.authBaseUrl}/auth/me`, {
      headers: { Authorization: `Bearer ${session.accessToken}` },
    });
    if (resp.status === 401) {
      // Bearer rejected — try one refresh + retry, then give up.
      try {
        const refreshed = await this.refresh();
        const retry = await this.fetchImpl(`${this.options.authBaseUrl}/auth/me`, {
          headers: { Authorization: `Bearer ${refreshed.accessToken}` },
        });
        if (!retry.ok) return null;
        return (await retry.json()) as MeResponse;
      } catch {
        return null;
      }
    }
    if (!resp.ok) {
      throw new Error(`me_failed_${resp.status}`);
    }
    return (await resp.json()) as MeResponse;
  }

  /// GET /auth/identities — list every sign-in method linked to the
  /// current user. Used by the /me page's Linked-accounts panel to
  /// know which "Add Apple / Google / email" buttons to show vs grey
  /// out, and which existing identities can be unlinked.
  async identities(): Promise<IdentityRow[]> {
    const session = this.session();
    if (!session) throw new Error("no_session");
    const resp = await this.fetchImpl(`${this.options.authBaseUrl}/auth/identities`, {
      headers: { Authorization: `Bearer ${session.accessToken}` },
    });
    if (!resp.ok) {
      throw new Error(`identities_failed_${resp.status}`);
    }
    const json = (await resp.json()) as { identities: IdentityRow[] };
    return json.identities;
  }

  /// Begin an account-link flow against `provider`. POSTs to
  /// `/auth/link/intent` to get a short-lived JWT, then for Apple +
  /// Google builds the start URL with `?linkIntent=...&returnUrl=...`
  /// and the caller navigates the browser to it. For magic-link the
  /// caller drops the returned `linkIntent` into a subsequent
  /// `requestMagicLink(...)` call.
  async beginLink(
    provider: "apple" | "google" | "magic_link",
  ): Promise<{ redirectUrl: string; linkIntent: string } | { linkIntent: string }> {
    const session = this.session();
    if (!session) throw new Error("no_session");
    const resp = await this.fetchImpl(`${this.options.authBaseUrl}/auth/link/intent`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${session.accessToken}`,
      },
      body: JSON.stringify({}),
    });
    if (!resp.ok) {
      throw new Error(`link_intent_failed_${resp.status}`);
    }
    const json = (await resp.json()) as LinkIntentResponse;
    if (provider === "magic_link") {
      return { linkIntent: json.linkIntent };
    }
    const startPath =
      provider === "apple" ? "/auth/apple/web/start" : "/auth/google/start";
    const params = new URLSearchParams({
      app: this.options.app,
      returnUrl: this.options.returnUrl,
      linkIntent: json.linkIntent,
    });
    return {
      redirectUrl: `${this.options.authBaseUrl}${startPath}?${params.toString()}`,
      linkIntent: json.linkIntent,
    };
  }

  /// DELETE /auth/identities/:provider/:subject — unlink an identity
  /// from the current user. The worker refuses to remove the last
  /// remaining identity (would orphan the user from every sign-in
  /// path), surfacing that as a 400 — caller maps to a UI message.
  async unlinkIdentity(provider: string, subject: string): Promise<void> {
    const session = this.session();
    if (!session) throw new Error("no_session");
    const resp = await this.fetchImpl(
      `${this.options.authBaseUrl}/auth/identities/${encodeURIComponent(provider)}/${encodeURIComponent(subject)}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session.accessToken}` },
      },
    );
    if (!resp.ok) {
      const body = await safeJson(resp);
      throw new Error(
        `unlink_failed_${resp.status}: ${JSON.stringify(body)}`,
      );
    }
  }
}

/// Parse the URL-fragment shape that goastern-auth's GET
/// `/auth/magic/redeem/:token` writes when `returnUrl` is set:
///   #access=<jwt>&refresh=<rt>&expires=<unix>&audience=<aud>
/// Returns null when the hash is absent or doesn't have the required
/// fields. The caller decides whether to clear the hash.
export function parseCallbackFragment(
  url: URL = new URL(typeof window !== "undefined" ? window.location.href : "http://localhost/"),
): CallbackFragment | null {
  const hash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  if (hash.length === 0) return null;
  const params = new URLSearchParams(hash);
  const access = params.get("access");
  const refresh = params.get("refresh");
  const expires = params.get("expires");
  const audience = params.get("audience");
  if (!access || !refresh || !expires || !audience) return null;
  const expiresAt = Number(expires);
  if (!Number.isFinite(expiresAt)) return null;
  return {
    accessToken: access,
    refreshToken: refresh,
    expiresAt,
    audience,
  };
}

async function safeJson(resp: Response): Promise<unknown> {
  try {
    return await resp.json();
  } catch {
    return { _nonJson: true };
  }
}
